"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
/**
 * Copyright 2025 Scott Bender (scott@scottbender.net)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const react_1 = require("react");
const reactstrap_1 = require("reactstrap");
const canboatjs_1 = require("@canboat/canboatjs");
const ts_pgns_1 = require("@canboat/ts-pgns");
const rxjs_1 = require("rxjs");
const SentencePanel_1 = require("./SentencePanel");
const localStorage_1 = require("../utils/localStorage");
const services_1 = require("../services");
const TransformTab = ({ isEmbedded = false }) => {
    // Create BehaviorSubject instances for SentencePanel (replays last value to new subscribers)
    const selectedPgnSubject = (0, react_1.useMemo)(() => new rxjs_1.BehaviorSubject(null), []);
    const deviceInfoSubject = (0, react_1.useMemo)(() => new rxjs_1.BehaviorSubject({}), []);
    // Create filtered subject that only emits valid PGN values (no nulls)
    const validPgnSubject = (0, react_1.useMemo)(() => new rxjs_1.BehaviorSubject(undefined), []);
    // Create our own parser instance with appropriate options
    const parser = (0, react_1.useMemo)(() => {
        const newParser = new canboatjs_1.FromPgn({
            returnNulls: true,
            checkForInvalidFields: true,
            useCamel: true,
            useCamelCompat: false,
            returnNonMatches: true,
            createPGNObjects: true,
            includeInputData: false,
            includeRawData: true,
            includeByteMapping: true,
        });
        newParser.on('error', (pgn, error) => {
            console.error(`TransformTab parser error for PGN ${pgn.pgn}:`, error);
        });
        newParser.on('warning', (pgn, warning) => {
            console.warn(`TransformTab parser warning for PGN ${pgn.pgn}:`, warning);
        });
        return newParser;
    }, []);
    // Load initial values from localStorage
    const [inputValue, setInputValue] = (0, react_1.useState)(() => {
        return localStorage_1.transformTabStorage.getInputValue();
    });
    const [parsedResult, setParsedResult] = (0, react_1.useState)(null);
    const [parseError, setParseError] = (0, react_1.useState)(null);
    const [signalKResult, setSignalKResult] = (0, react_1.useState)(null);
    const [signalKLoading, setSignalKLoading] = (0, react_1.useState)(false);
    const [outputFormat, setOutputFormat] = (0, react_1.useState)(() => {
        return localStorage_1.transformTabStorage.getOutputFormat();
    });
    const [messageHistory, setMessageHistory] = (0, react_1.useState)([]);
    const [showHistoryDropdown, setShowHistoryDropdown] = (0, react_1.useState)(false);
    // Save inputValue to localStorage whenever it changes
    (0, react_1.useEffect)(() => {
        localStorage_1.transformTabStorage.setInputValue(inputValue);
    }, [inputValue]);
    // Save outputFormat to localStorage whenever it changes
    (0, react_1.useEffect)(() => {
        localStorage_1.transformTabStorage.setOutputFormat(outputFormat);
    }, [outputFormat]);
    // Load message history from localStorage on component mount
    (0, react_1.useEffect)(() => {
        const savedHistory = localStorage_1.transformTabStorage.getMessageHistory();
        // Convert MessageHistoryItem[] to MessageHistory[] (ensure format is string)
        const convertedHistory = savedHistory.map((item) => ({
            message: item.message,
            timestamp: item.timestamp,
            format: item.format || 'unknown',
        }));
        setMessageHistory(convertedHistory);
    }, []);
    // Save message history to localStorage whenever it changes
    (0, react_1.useEffect)(() => {
        localStorage_1.transformTabStorage.setMessageHistory(messageHistory);
    }, [messageHistory]);
    // Function to detect message format
    const detectMessageFormat = (message) => {
        const trimmed = message.trim();
        if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
            return 'JSON';
        }
        else {
            return 'String';
        }
    };
    // Function to get a readable description for a message
    const getMessageDescription = (message, format) => {
        // For multi-line messages, only show the first line
        const firstLine = message.split('\n')[0];
        if (format !== 'JSON') {
            return firstLine;
        }
        try {
            const parsed = JSON.parse(message);
            if (Array.isArray(parsed)) {
                // For arrays, show count and first PGN info
                if (parsed.length > 0 && parsed[0].pgn) {
                    const firstPgn = parsed[0];
                    const desc = firstPgn.description || `PGN ${firstPgn.pgn}`;
                    return `${parsed.length} messages: ${desc}${parsed.length > 1 ? ', ...' : ''}`;
                }
                return `${parsed.length} messages`;
            }
            else {
                // For single objects, show PGN description
                if (parsed.pgn) {
                    return parsed.description || `PGN ${parsed.pgn}`;
                }
                return 'JSON message';
            }
        }
        catch (error) {
            return firstLine;
        }
    };
    // Function to add message to history
    const addToHistory = (message) => {
        if (!message.trim())
            return;
        const historyItem = {
            message,
            timestamp: new Date().toISOString(),
            format: detectMessageFormat(message),
        };
        setMessageHistory((prev) => {
            // Remove duplicate if exists
            const filtered = prev.filter((item) => item.message !== message);
            // Add new item at the beginning and limit to 20 items
            return [historyItem, ...filtered].slice(0, 20);
        });
    };
    // Function to select message from history
    const selectFromHistory = (message) => {
        setInputValue(message);
        setShowHistoryDropdown(false);
    };
    // Reset history navigation when input changes manually
    const handleInputChange = (event) => {
        setInputValue(event.target.value);
    };
    // Function to clear history
    const clearHistory = () => {
        if (messageHistory.length > 0 && !confirm('Are you sure you want to clear all message history?')) {
            return;
        }
        setMessageHistory([]);
        setShowHistoryDropdown(false);
    };
    // Function to delete a specific history item
    const deleteHistoryItem = (index, event) => {
        event.stopPropagation(); // Prevent selecting the item when deleting
        setMessageHistory((prev) => prev.filter((_, i) => i !== index));
    };
    // Function to transform to SignalK using the API endpoint
    const transformToSignalK = async (pgn) => {
        try {
            setSignalKLoading(true);
            const result = await services_1.server.post({ type: 'n2k-signalk', values: [pgn] });
            if (result.signalKDeltas && result.signalKDeltas.length > 0) {
                return JSON.stringify(result.signalKDeltas, null, 2);
            }
            else {
                return 'No SignalK deltas generated';
            }
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            return `Error transforming to SignalK: ${errorMessage}`;
        }
        finally {
            setSignalKLoading(false);
        }
    };
    // Auto-parse whenever input, parser, or output format changes
    (0, react_1.useEffect)(() => {
        if (!inputValue.trim()) {
            setParsedResult(null);
            setParseError(null);
            selectedPgnSubject.next(null);
            validPgnSubject.next(undefined);
            return;
        }
        try {
            setParseError(null);
            // Try to parse as JSON first
            if (inputValue.trim().startsWith('{')) {
                const jsonData = JSON.parse(inputValue);
                // If it's already a valid PGN JSON object, use it directly
                if (jsonData.pgn && typeof jsonData.pgn === 'number') {
                    setParsedResult(jsonData);
                    selectedPgnSubject.next(jsonData);
                    validPgnSubject.next(jsonData);
                    // Add successful parse to history
                    addToHistory(inputValue);
                }
                else {
                    setParseError('Invalid PGN JSON format - missing required pgn field');
                    selectedPgnSubject.next(null);
                    validPgnSubject.next(undefined);
                }
                return;
            }
            // Try to parse as string format using the parser if available
            if (parser) {
                const lines = inputValue.split('\n').filter((line) => line.trim());
                let result = undefined;
                parser.options.useCamel = outputFormat === 'canboat-json-camel' || outputFormat === 'signalk';
                for (const line of lines) {
                    result = parser.parseString(line);
                    if (result) {
                        setParsedResult(result);
                        selectedPgnSubject.next(result);
                        validPgnSubject.next(result);
                        // Add successful parse to history
                        addToHistory(inputValue);
                        break;
                    }
                }
                if (!result) {
                    setParseError('Failed to parse message - invalid format or unsupported PGN');
                    selectedPgnSubject.next(null);
                    validPgnSubject.next(undefined);
                }
            }
            else {
                setParseError('Parser not available');
                selectedPgnSubject.next(null);
                validPgnSubject.next(undefined);
            }
        }
        catch (error) {
            console.error('Error parsing input value:', error);
            setParseError(`Parse error: ${error instanceof Error ? error.message : 'Unknown error'}`);
            selectedPgnSubject.next(null);
            validPgnSubject.next(undefined);
        }
    }, [inputValue, parser, outputFormat]);
    // Handle SignalK transformation when format changes to signalk
    (0, react_1.useEffect)(() => {
        if (outputFormat === 'signalk' && parsedResult) {
            transformToSignalK(parsedResult).then((result) => {
                setSignalKResult(result);
            });
        }
        else {
            setSignalKResult(null);
        }
    }, [outputFormat, parsedResult]);
    const outputFormats = [
        { value: 'canboat-json', label: 'Canboat JSON' },
        { value: 'canboat-json-camel', label: 'Canboat JSON (Camel Case)' },
        { value: 'actisense', label: 'Actisense Serial' },
        { value: 'actisense-n2k-ascii', label: 'Actisense N2K ASCII' },
        { value: 'ikonvert', label: 'iKonvert' },
        { value: 'ydwg-full-raw', label: 'Yacht Devices RAW' },
        { value: 'ydwg-raw', label: 'Yacht Devices RAW Send' },
        { value: 'pcdin', label: 'PCDIN' },
        { value: 'mxpgn', label: 'MXPGN' },
        { value: 'candump1', label: 'Linux CAN utils (Angstrom)' },
        { value: 'candump2', label: 'Linux CAN utils (Debian)' },
        { value: 'candump3', label: 'Linux CAN utils (log format)' },
        { value: 'signalk', label: 'Signal K' },
    ];
    const formatOutput = (pgn, format) => {
        if (!pgn)
            return '';
        try {
            switch (format) {
                case 'canboat-json':
                case 'canboat-json-camel':
                    delete pgn.input;
                    return JSON.stringify(pgn, null, 2);
                case 'actisense':
                    // Use canboatjs built-in function
                    const actisenseResult = (0, canboatjs_1.pgnToActisenseSerialFormat)(pgn);
                    return actisenseResult || 'Unable to format to Actisense format';
                case 'ikonvert':
                    // Use canboatjs built-in function
                    const ikonvertResult = (0, canboatjs_1.pgnToiKonvertSerialFormat)(pgn);
                    return ikonvertResult || 'Unable to format to iKonvert format';
                case 'ydwg-raw':
                    // Use canboatjs built-in function - this returns an array of strings
                    const ydwgResult = (0, canboatjs_1.pgnToYdgwRawFormat)(pgn);
                    if (Array.isArray(ydwgResult)) {
                        return ydwgResult.join('\n');
                    }
                    return ydwgResult || 'Unable to format to YDWG Raw format';
                case 'ydwg-full-raw':
                    // Use canboatjs built-in function - this returns an array of strings
                    const ydwgFullResult = (0, canboatjs_1.pgnToYdgwFullRawFormat)(pgn);
                    if (Array.isArray(ydwgFullResult)) {
                        return ydwgFullResult.join('\n');
                    }
                    return ydwgFullResult || 'Unable to format to YDWG Full Raw format';
                case 'actisense-n2k-ascii':
                    // Use canboatjs built-in function
                    const n2kAsciiResult = (0, canboatjs_1.pgnToActisenseN2KAsciiFormat)(pgn);
                    return n2kAsciiResult || 'Unable to format to Actisense N2K ASCII format';
                case 'pcdin':
                    // Use canboatjs built-in function
                    const pcdinResult = (0, canboatjs_1.pgnToPCDIN)(pgn);
                    return pcdinResult || 'Unable to format to PCDIN format';
                case 'mxpgn':
                    // Use canboatjs built-in function
                    const mxpgnResult = (0, canboatjs_1.pgnToMXPGN)(pgn);
                    return mxpgnResult || 'Unable to format to MXPGN format';
                case 'candump1':
                    // Use canboatjs built-in function - this returns an array of strings
                    const candump1Result = (0, canboatjs_1.pgnToCandump1)(pgn);
                    if (Array.isArray(candump1Result)) {
                        return candump1Result.join('\n');
                    }
                    return candump1Result || 'Unable to format to candump1 format';
                case 'candump2':
                    // Use canboatjs built-in function - this returns an array of strings
                    const candump2Result = (0, canboatjs_1.pgnToCandump2)(pgn);
                    if (Array.isArray(candump2Result)) {
                        return candump2Result.join('\n');
                    }
                    return candump2Result || 'Unable to format to candump2 format';
                case 'candump3':
                    // Use canboatjs built-in function - this returns an array of strings
                    const candump3Result = (0, canboatjs_1.pgnToCandump3)(pgn);
                    if (Array.isArray(candump3Result)) {
                        return candump3Result.join('\n');
                    }
                    return candump3Result || 'Unable to format to candump3 format';
                case 'signalk':
                    // Return the cached SignalK result or loading message
                    if (signalKLoading) {
                        return 'Loading SignalK transformation...';
                    }
                    return signalKResult || 'SignalK transformation not available';
                default:
                    return JSON.stringify(pgn, null, 2);
            }
        }
        catch (error) {
            return `Error formatting output: ${error instanceof Error ? error.message : 'Unknown error'}`;
        }
    };
    const handleClear = () => {
        setInputValue('');
        setParsedResult(null);
        setParseError(null);
        setSignalKResult(null);
        // Clear the subjects as well
        selectedPgnSubject.next(null);
        validPgnSubject.next(undefined);
    };
    const handleCopyOutput = async () => {
        if (!parsedResult)
            return;
        let outputText;
        if (outputFormat === 'signalk') {
            outputText = signalKResult || 'SignalK transformation not available';
        }
        else {
            outputText = formatOutput(parsedResult, outputFormat);
        }
        try {
            await navigator.clipboard.writeText(outputText);
            // Optional: You could add a toast notification here
        }
        catch (error) {
            console.error('Failed to copy to clipboard:', error);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = outputText;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
        }
    };
    return ((0, jsx_runtime_1.jsx)(reactstrap_1.Card, { children: (0, jsx_runtime_1.jsxs)(reactstrap_1.CardBody, { children: [(0, jsx_runtime_1.jsx)("h4", { className: "text-sk-primary", children: "Data Transformation" }), (0, jsx_runtime_1.jsx)("p", { className: "mb-3", children: "Transform and convert NMEA 2000 data between different formats and protocols." }), (0, jsx_runtime_1.jsxs)("div", { className: "row mb-4", children: [(0, jsx_runtime_1.jsx)("div", { className: "col-md-6", children: (0, jsx_runtime_1.jsxs)("div", { className: "card", children: [(0, jsx_runtime_1.jsx)("div", { className: "card-header", children: (0, jsx_runtime_1.jsx)("h6", { className: "mb-0", children: "NMEA 2000 Message Input" }) }), (0, jsx_runtime_1.jsxs)("div", { className: "card-body", children: [(0, jsx_runtime_1.jsxs)("div", { className: "form-group", children: [(0, jsx_runtime_1.jsxs)("div", { className: "d-flex justify-content-between align-items-center mb-2", children: [(0, jsx_runtime_1.jsx)("div", { children: (0, jsx_runtime_1.jsx)("label", { htmlFor: "nmea2000Input", className: "form-label mb-0", children: "Enter NMEA 2000 message (String or JSON format):" }) }), messageHistory.length > 0 && ((0, jsx_runtime_1.jsxs)("div", { className: "position-relative", children: [(0, jsx_runtime_1.jsxs)("button", { type: "button", className: "btn btn-outline-secondary btn-sm", onClick: () => setShowHistoryDropdown(!showHistoryDropdown), children: [(0, jsx_runtime_1.jsx)("i", { className: "fas fa-history me-1" }), "History (", messageHistory.length, ")"] }), showHistoryDropdown && ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("div", { className: "position-fixed top-0 start-0 w-100 h-100", style: { zIndex: 1040 }, onClick: () => setShowHistoryDropdown(false) }), (0, jsx_runtime_1.jsxs)("div", { className: "position-absolute bg-white border rounded shadow-lg", style: {
                                                                                    top: '100%',
                                                                                    right: 0,
                                                                                    minWidth: '400px',
                                                                                    maxHeight: '300px',
                                                                                    overflowY: 'auto',
                                                                                    zIndex: 1050,
                                                                                    marginTop: '4px',
                                                                                }, children: [(0, jsx_runtime_1.jsx)("div", { className: "p-2 border-bottom bg-light", children: (0, jsx_runtime_1.jsxs)("div", { className: "d-flex justify-content-between align-items-center", children: [(0, jsx_runtime_1.jsx)("small", { className: "text-muted fw-bold", children: "Message History" }), (0, jsx_runtime_1.jsx)("div", { className: "d-flex gap-2", children: (0, jsx_runtime_1.jsxs)("button", { type: "button", className: "btn btn-outline-danger btn-sm", onClick: clearHistory, title: "Clear all history", style: { fontSize: '0.75em', padding: '2px 8px' }, children: [(0, jsx_runtime_1.jsx)("i", { className: "fas fa-trash me-1" }), "Clear All"] }) })] }) }), messageHistory.map((item, index) => ((0, jsx_runtime_1.jsx)("div", { className: "p-2 border-bottom cursor-pointer", style: {
                                                                                            cursor: 'pointer',
                                                                                            transition: 'background-color 0.2s',
                                                                                        }, onClick: () => selectFromHistory(item.message), onMouseEnter: (e) => {
                                                                                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                                                                                        }, onMouseLeave: (e) => {
                                                                                            e.currentTarget.style.backgroundColor = 'transparent';
                                                                                        }, children: (0, jsx_runtime_1.jsxs)("div", { className: "d-flex justify-content-between align-items-start", children: [(0, jsx_runtime_1.jsxs)("div", { className: "flex-grow-1", style: { minWidth: 0 }, children: [(0, jsx_runtime_1.jsxs)("div", { className: "d-flex align-items-center mb-1", children: [(0, jsx_runtime_1.jsx)("span", { className: "badge bg-secondary me-2", style: { fontSize: '0.7em' }, children: item.format }), (0, jsx_runtime_1.jsx)("small", { className: "text-muted", children: new Date(item.timestamp).toLocaleString() })] }), (0, jsx_runtime_1.jsx)("div", { className: "font-monospace small text-truncate", style: { fontSize: '0.75em' }, title: item.message, children: getMessageDescription(item.message, item.format) })] }), (0, jsx_runtime_1.jsx)("button", { type: "button", className: "btn btn-sm text-danger p-1", onClick: (e) => deleteHistoryItem(index, e), title: "Delete this item", style: {
                                                                                                        fontSize: '14px',
                                                                                                        lineHeight: 1,
                                                                                                        minWidth: '22px',
                                                                                                        height: '22px',
                                                                                                        display: 'flex',
                                                                                                        alignItems: 'center',
                                                                                                        justifyContent: 'center',
                                                                                                        border: '1px solid #dc3545',
                                                                                                        borderRadius: '3px',
                                                                                                        backgroundColor: 'white',
                                                                                                        fontWeight: 'bold',
                                                                                                    }, children: "\u00D7" })] }) }, index)))] })] }))] }))] }), (0, jsx_runtime_1.jsx)("textarea", { id: "nmea2000Input", className: "form-control mb-3", rows: 12, value: inputValue, onChange: handleInputChange, placeholder: 'Enter your NMEA 2000 message here (auto-parsed)...\nExamples:\nString format: 2023-10-15T10:30:45.123Z,2,127250,17,255,8,00,fc,69,97,00,00,00,00\nCanboat JSON format: {"timestamp": "2023-10-15T10:30:45.123Z", "prio": 2, "src": 17, "dst": 255, "pgn": 127250, "description": "Vessel Heading", "fields": {"SID": 0, "Heading": 1.5708, "Deviation": null, "Variation": null, "Reference": "Magnetic"}}', style: {
                                                            fontFamily: 'monospace',
                                                            whiteSpace: 'pre',
                                                            overflowX: 'auto',
                                                        }, spellCheck: false })] }), (0, jsx_runtime_1.jsx)("div", { className: "d-flex gap-2 mb-3", children: (0, jsx_runtime_1.jsx)("button", { className: "btn btn-secondary", type: "button", onClick: handleClear, children: "Clear" }) }), parseError && ((0, jsx_runtime_1.jsxs)("div", { className: "alert alert-danger", role: "alert", children: [(0, jsx_runtime_1.jsx)("strong", { children: "Error:" }), " ", parseError] }))] })] }) }), (0, jsx_runtime_1.jsx)("div", { className: "col-md-6", children: (0, jsx_runtime_1.jsxs)("div", { className: "card", children: [(0, jsx_runtime_1.jsx)("div", { className: "card-header", children: (0, jsx_runtime_1.jsx)("h6", { className: "mb-0", children: "Transformation Output" }) }), (0, jsx_runtime_1.jsxs)("div", { className: "card-body", children: [(0, jsx_runtime_1.jsxs)("div", { className: "form-group mb-3", children: [(0, jsx_runtime_1.jsx)("label", { htmlFor: "outputFormat", className: "form-label", children: "Output Format:" }), (0, jsx_runtime_1.jsx)("select", { id: "outputFormat", className: "form-control", value: outputFormat, onChange: (e) => setOutputFormat(e.target.value), children: outputFormats.map((format) => ((0, jsx_runtime_1.jsx)("option", { value: format.value, children: format.label }, format.value))) })] }), (0, jsx_runtime_1.jsxs)("div", { className: "form-group", children: [(0, jsx_runtime_1.jsxs)("div", { className: "d-flex justify-content-between align-items-center mb-2", children: [(0, jsx_runtime_1.jsx)("label", { htmlFor: "transformOutput", className: "form-label mb-0", children: "Parsed Result:" }), parsedResult &&
                                                                (!(parsedResult instanceof ts_pgns_1.PGN) ||
                                                                    (outputFormat !== 'canboat-json' && outputFormat !== 'canboat-json-camel')) && ((0, jsx_runtime_1.jsx)("button", { className: "btn btn-outline-primary btn-sm", type: "button", onClick: handleCopyOutput, title: "Copy output to clipboard", children: "Copy" }))] }), parsedResult instanceof ts_pgns_1.PGN &&
                                                        (outputFormat === 'canboat-json' || outputFormat === 'canboat-json-camel') ? ((0, jsx_runtime_1.jsx)("div", { style: { border: '1px solid #ced4da', borderRadius: '0.25rem', backgroundColor: '#f8f9fa' }, children: (0, jsx_runtime_1.jsx)(SentencePanel_1.SentencePanel, { selectedPgn: validPgnSubject, info: deviceInfoSubject, inEditingTab: false }) })) : ((0, jsx_runtime_1.jsx)("textarea", { id: "transformOutput", className: "form-control", rows: 9, value: parsedResult ? formatOutput(parsedResult, outputFormat) : '', readOnly: true, placeholder: "Parsed message will appear here...", style: { fontFamily: 'monospace', backgroundColor: '#f8f9fa' } }))] }), parsedResult && ((0, jsx_runtime_1.jsx)("div", { className: "mt-3", children: (0, jsx_runtime_1.jsx)("div", { className: "row", children: (0, jsx_runtime_1.jsx)("div", { className: "col-12", children: (0, jsx_runtime_1.jsx)("div", { className: "card bg-light", children: (0, jsx_runtime_1.jsx)("div", { className: "card-body p-2", children: (0, jsx_runtime_1.jsxs)("small", { children: [(0, jsx_runtime_1.jsx)("strong", { children: "PGN:" }), " ", parsedResult.pgn, " |", (0, jsx_runtime_1.jsx)("strong", { children: " Source:" }), " ", parsedResult.src, " |", (0, jsx_runtime_1.jsx)("strong", { children: " Priority:" }), " ", parsedResult.prio, (0, jsx_runtime_1.jsx)("br", {}), (0, jsx_runtime_1.jsx)("strong", { children: "Description:" }), " ", parsedResult.description || 'N/A'] }) }) }) }) }) }))] })] }) })] })] }) }));
};
exports.default = TransformTab;
